import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface Props {
  // Hold the sheet off-screen until the content behind it has arrived. A sheet
  // that slides in and then grows as its data lands jumps twice.
  isReady?: boolean;
  onClose: () => void;
}

/**
 * The shared bottom sheet: renders its content off-screen, then slides it up
 * once it is complete, and slides it back down before unmounting.
 *
 * Every bottom sheet in the app goes through this -- the payout screens and the
 * employee page's Slack picker so far -- so they all behave the same way. Do not
 * hand-roll one: the copies that existed painted a Loader (a page-level,
 * position: fixed band) before their data, then grew; and centred with
 * translateX(-50%), which a keyframe slide overrides, so they rose off-centre and
 * jumped. The backdrop fades in immediately -- a tap has to feel like it
 * landed even when the content is still loading.
 *
 * Rendered through a portal on purpose. A sheet can open on top of another one
 * (adding an expense from inside a payee sheet), and the `transform` that drives
 * the slide makes an element the containing block for `position: fixed`
 * descendants -- so a nested sheet would be positioned against its parent and
 * clipped by its scroll box instead of filling the viewport.
 */
const BottomSheet: React.FC<Props> = ({ isReady = true, onClose, children }) => {
  const [isIn, setIsIn] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Two frames: the first paints the sheet in its off-screen position, the
  // second flips the transform so the browser has something to animate from.
  // One frame is not enough -- the transition gets skipped and it snaps.
  useEffect(() => {
    if (!isReady || isClosing) return undefined;

    let second: number;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setIsIn(true));
    });

    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
  }, [isReady, isClosing]);

  const close = (e: React.MouseEvent) => {
    // A nested sheet must not close the one underneath it as well.
    e.stopPropagation();

    // Never slid in, so there is no transition to wait for.
    if (!isIn) {
      onClose();
      return;
    }

    setIsClosing(true);
    setIsIn(false);
  };

  return createPortal(
    <Overlay isIn={isIn} onClick={close}>
      <Sheet
        isIn={isIn}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={(e) => {
          if (isClosing && e.propertyName === 'transform') onClose();
        }}
      >
        <Handle />
        {children}
      </Sheet>
    </Overlay>,
    document.body
  );
};

const Overlay = styled.div<{ isIn: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
  background: rgba(33, 39, 46, ${({ isIn }) => (isIn ? 0.4 : 0)});
  transition: background 220ms ease;
`;

const Sheet = styled.div<{ isIn: boolean }>`
  width: 100%;
  max-width: 552px;
  background: #ffffff;
  border-radius: 12px 12px 0 0;
  padding: 10px 16px 24px;
  max-height: 88%;
  overflow-y: auto;
  transform: translateY(${({ isIn }) => (isIn ? '0' : '100%')});
  transition: transform 260ms cubic-bezier(0.32, 0.72, 0, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Handle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #dae1e8;
  margin: 0 auto 12px;
`;

export default BottomSheet;
